import { isNotDefined } from '@sgme/fp';
import classnames from 'classnames';
import { ProductNodeBuilderProps } from 'components/common-form/builder/form/node/ProductNodeBuilder.model';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { ListField } from 'components/shared/ListField/ListField';
import { NumberFormatter } from 'components/shared/NumberFormatter/NumberFormatter';
import { UnderlyingsLabelField } from 'components/shared/UnderlyingsLabelField/UnderlyingsLabelField';
import type { IntlKey } from 'locales';
import type { UnderlyingsStrikeFieldData, UnderlyingsStrikeFieldMetadata } from 'models/forms/fields';
import { AvailableItem } from 'models/forms/fields/AvailableItemsField';
import { UnderlyingCodeType } from 'models/underlying/base';
import { EquityUnderlying } from 'models/underlying/equity';
import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getTextAlignClassName } from 'shared/utils/getTextAlignClassName';
import styled from 'styled-components';

export const StyledUnderlyingsStrikeFieldWrapper = styled.div`
  & > .underlyings-label-field-component {
    flex: 36;
  }

  & > .list-field-component {
    flex: 36;
  }

  & > .text-field-component {
    flex: 28;
  }
`;

export const UnderlyingsStrikeField: FC<ProductNodeBuilderProps> = ({ node: { id }, className, workspaceType }) => {
  const fieldMetadata = useFieldMetadata<UnderlyingsStrikeFieldMetadata>(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const {
    underlyingIndex,
    textAlign,
    codeType,
    availableStrikeModes,
    isStrikeModeReadOnly,
    isStrikeValueReadOnly,
    isReadOnly,
    nbDecimals,
    translationKey,
  } = useMemo(() => {
    if (isNotDefined(fieldMetadata)) {
      return { codeType: 'Unknown' as UnderlyingCodeType, availableStrikeModes: [] };
    }
    const {
      underlyingIndex,
      textAlign,
      codeType,
      availableStrikeModes,
      isStrikeModeReadOnly,
      isStrikeValueReadOnly,
      isReadOnly,
      nbDecimals,
      translationKey,
    } = fieldMetadata;
    return {
      underlyingIndex,
      textAlign,
      codeType,
      availableStrikeModes,
      isStrikeModeReadOnly,
      isStrikeValueReadOnly,
      isReadOnly,
      nbDecimals,
      translationKey,
    };
  }, [fieldMetadata]);

  const [underlyingsFieldData, setUnderlyingsFieldData] = useData<UnderlyingsStrikeFieldData>(id);
  const underlying = useMemo(
    () => getUnderlying(underlyingsFieldData, underlyingIndex),
    [underlyingsFieldData, underlyingIndex],
  );
  const strikeMode = useMemo(() => underlying?.strike?.strikeMode, [underlying]);
  const strikeValue = useMemo(() => underlying?.strike?.strikeValue, [underlying]);
  const textAlignClassName = useMemo(() => getTextAlignClassName(textAlign), [textAlign]);

  const intlPrefixer = (translationKey: string) => `${workspaceType}.${translationKey}`;

  const { formatMessage } = useIntl();
  const translatedAvailableStrikeModes = availableStrikeModes?.map((item: AvailableItem) => ({
    label: formatMessage({
      id: intlPrefixer(`StrikeMode.${item.label}`) as IntlKey,
      defaultMessage: item.label,
    }),
    value: item.value,
  }));

  const onStrikeModeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      if (!underlyingsFieldData) {
        return;
      }
      const newStrikeMode = event.target.value;
      const underlyings = getUnderlyingsOnStrikeModeChange(underlyingsFieldData, newStrikeMode, underlyingIndex);
      setUnderlyingsFieldData(underlyings);
    },
    [setUnderlyingsFieldData, underlyingsFieldData, underlyingIndex],
  );

  const onStrikeValueChange = useCallback(
    (newStrikeValue?: number) => {
      if (!underlyingsFieldData || newStrikeValue === strikeValue) {
        return;
      }
      const underlyings = getUnderlyingsOnStrikeValueChange(underlyingsFieldData, newStrikeValue, underlyingIndex);
      setUnderlyingsFieldData(underlyings);
    },
    [setUnderlyingsFieldData, underlyingsFieldData, underlyingIndex, strikeValue],
  );

  return underlying !== undefined ? (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      minWidth={fieldMetadata?.minWidth}
      maxWidth={fieldMetadata?.maxWidth}
      className={patchedFieldClassName}
    >
      <StyledUnderlyingsStrikeFieldWrapper
        className={classnames('d-flex flex-row align-items-center justify-content-center form-group mb-0', className)}
      >
        <UnderlyingsLabelField e2e={id ? `${id}-code` : ''} value={underlying} codeType={codeType} />

        <ListField
          id={id ? `${id}-strike-mode` : ''}
          items={translatedAvailableStrikeModes}
          value={strikeMode}
          isReadOnly={isStrikeModeReadOnly || isReadOnly}
          onChange={onStrikeModeChange}
          hasEmptyItem
        />

        <NumberFormatter
          e2e={id ? `${id}-strike-value` : ''}
          value={strikeValue}
          precision={nbDecimals}
          decimalPadding
          isReadOnly={isStrikeValueReadOnly || isReadOnly}
          onBlur={onStrikeValueChange}
          className={textAlignClassName}
        />
      </StyledUnderlyingsStrikeFieldWrapper>
    </BlockOrientationFormGroup>
  ) : null;
};

function getUnderlyingsOnStrikeModeChange(
  underlyings: readonly EquityUnderlying[],
  newStrikeMode: string,
  underlyingIndex?: number,
): EquityUnderlying[] {
  return underlyings.map((udl, index) =>
    index === underlyingIndex
      ? {
          ...udl,
          strike: {
            ...udl.strike,
            strikeMode: newStrikeMode,
          },
        }
      : udl,
  ) as EquityUnderlying[];
}

function getUnderlyingsOnStrikeValueChange(
  underlyings: readonly EquityUnderlying[],
  newStrikeValue?: number,
  underlyingIndex?: number,
): EquityUnderlying[] {
  return underlyings.map((udl, index) =>
    index === underlyingIndex
      ? {
          ...udl,
          strike: {
            ...udl.strike,
            strikeValue: newStrikeValue,
          },
        }
      : udl,
  ) as EquityUnderlying[];
}

function getUnderlying(
  underlyings: readonly EquityUnderlying[],
  underlyingIndex: number | undefined,
): EquityUnderlying | undefined {
  return underlyings ? underlyings[underlyingIndex ?? 0] : undefined;
}
